// Google Analytics 4 utility functions
import type { SupabaseUser } from '../types/supabase';

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

export const GA_TRACKING_ID = 'G-D1LB902T06';

interface EventParams {
  action: string;
  category: string;
  label?: string;
  value?: number;
  userId?: string;
  userEmail?: string;
}

// Set user properties for analytics
export const setUserProperties = (userId: string, userEmail?: string, userName?: string): void => {
  if (typeof window !== 'undefined' && window.gtag) {
    
    window.gtag('config', GA_TRACKING_ID, {
      user_id: userId,
      custom_map: {
        user_email: userEmail,
        user_name: userName
      }
    });
    
    // Set user properties
    window.gtag('set', {
      user_id: userId,
      user_email: userEmail,
      user_name: userName
    });
    
  } else {
    console.warn('⚠️ gtag not available for setting user properties');
  }
};

// Clear user properties (on logout)
export const clearUserProperties = (): void => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', GA_TRACKING_ID, {
      user_id: null
    });
  }
};

// https://developers.google.com/analytics/devguides/collection/gtagjs/pages
export const pageview = (url: string): void => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', GA_TRACKING_ID, {
      page_path: url,
    });
  }
};

// Enhanced event tracking with user context
export const event = ({ action, category, label, value, userId, userEmail }: EventParams): void => {
  if (typeof window !== 'undefined' && window.gtag) {
    const eventData: Record<string, any> = {
      event_category: category,
      event_label: label,
      value: value,
    };
    
    // Add user context if provided
    if (userId) {
      eventData.user_id = userId;
      console.log(`📊 Event with user ID: ${action} (User: ${userId})`);
    }
    if (userEmail) {
      eventData.user_email = userEmail;
    }
    
    window.gtag('event', action, eventData);
  }
};

// Custom events for Lyric Boost app with user context
export const trackSongCreation = (songType: string = 'unknown', user: SupabaseUser | null = null): void => {
  event({
    action: 'song_created',
    category: 'engagement',
    label: songType,
    userId: user?.id,
    userEmail: user?.email,
  });
};

export const trackAudioUpload = (user: SupabaseUser | null = null): void => {
  event({
    action: 'audio_uploaded',
    category: 'engagement',
    label: 'audio_feature',
    userId: user?.id,
    userEmail: user?.email,
  });
};

export const trackLyricGeneration = (method: string = 'unknown', user: SupabaseUser | null = null): void => {
  event({
    action: 'lyrics_generated',
    category: 'engagement',
    label: method,
    userId: user?.id,
    userEmail: user?.email,
  });
};

export const trackUserSignup = (method: string = 'email', user: SupabaseUser | null = null): void => {
  event({
    action: 'sign_up',
    category: 'authentication',
    label: method,
    userId: user?.id,
    userEmail: user?.email,
  });
};

export const trackUserLogin = (method: string = 'email', user: SupabaseUser | null = null): void => {
  event({
    action: 'login',
    category: 'authentication',
    label: method,
    userId: user?.id,
    userEmail: user?.email,
  });
};

export const trackPremiumUpgrade = (user: SupabaseUser | null = null): void => {
  event({
    action: 'premium_upgrade',
    category: 'conversion',
    label: 'subscription',
    userId: user?.id,
    userEmail: user?.email,
  });
};

export const trackFeatureUsage = (featureName: string, user: SupabaseUser | null = null): void => {
  event({
    action: 'feature_used',
    category: 'engagement',
    label: featureName,
    userId: user?.id,
    userEmail: user?.email,
  });
};